import { Injectable, Injector, inject } from '@angular/core';

import { AppConfigService } from '~/core/services/appconfig.service';
import { CONNECTED_FOLDER_KEY, FileExt, FoundDocument, DocType, L2_PROCESSES, Landscape, SOURCES } from '~/models/document-finder.models';

export interface AzureFolderSettings {
    /** https://<account>.blob.core.windows.net/<container> */
    containerUrl: string;
    /** Folder (blob prefix) inside the container, e.g. "Explore/L1 - Production Execution/" */
    prefix: string;
    /** Container SAS token with read + list permission (with or without the leading "?") */
    sasToken: string;
}

export interface AzureBlob { name: string; size: number; modified: string; }

const EXTENSIONS: FileExt[] = ['docx', 'xlsx', 'pdf', 'pptx', 'bpmn'];

/**
 * "Connect folder": lists the documents of an Azure Blob Storage folder (List Blobs REST call, no extra npm package),
 * classifies them into Document Finder documents and downloads a blob on demand.
 * The SAS token is kept for the browser session only; the container URL and folder are remembered locally.
 */
@Injectable({ providedIn: 'root' })
export class AzureStorageService {
    private injector = inject(Injector, { optional: true });
    private _settings: AzureFolderSettings | null = null;

    /** Container URL, folder and SAS token come from `azureStorage` in assets/config.json (loaded on first use). */
    public get settings(): AzureFolderSettings {
        return this._settings ??= this.loadSettings();
    }
    public set settings(v: AzureFolderSettings) { this._settings = v; }

    public get configured(): boolean {
        const s = this.settings;
        return !!s.containerUrl.trim() && !!s.sasToken.trim();
    }
    public connected = false;
    public busy = false;
    public error = '';
    public progress = '';
    public documents: FoundDocument[] = [];
    private urls = new Map<string, string>();

    //*************************************************************************
    //  Connect
    //*************************************************************************
    /** Connects to the folder and returns the classified documents. */
    public async connect(): Promise<FoundDocument[]> {
        this.error = '';
        const s = this.settings;
        if (!/^https:\/\/[^/]+\/[^/?]+/i.test(s.containerUrl.trim())) {
            throw this.fail('Azure Storage is not configured. Set azureStorage.containerUrl in assets/config.json');
        }
        if (!s.sasToken.trim()) {
            throw this.fail('Azure Storage is not configured. Set azureStorage.sasToken (read + list) in assets/config.json');
        }
        this.busy = true;
        this.progress = 'Connecting…';
        try {
            const blobs = await this.list();
            // Map blobs to documents, filter out nulls (unrecognized files)
            const allDocs = blobs.map((b, i) => this.toDocument(b, i)).filter((d): d is FoundDocument => !!d);
            // Filter out "other" type documents (unrecognized patterns)
            this.documents = allDocs.filter(d => d.t !== 'other');
            SOURCES[CONNECTED_FOLDER_KEY] = { name: 'Azure Storage · Connected folder', n: blobs.length };
            this.connected = true;
            this.progress = `${blobs.length} files in ${this.folderCount(blobs)} folders`;
            return this.documents;
        } catch (e: any) {
            this.connected = false;
            throw this.fail(e?.message || 'Could not connect to Azure Storage');
        } finally {
            this.busy = false;
        }
    }

    public disconnect(): void {
        this.connected = false;
        this.documents = [];
        this.urls.clear();
        this.progress = '';
        delete SOURCES[CONNECTED_FOLDER_KEY];
    }

    /** Downloads the blob behind a connected document, or null when it did not come from Azure Storage. */
    public async getBlob(id: string): Promise<Blob | null> {
        const url = this.urls.get(id);
        if (!url) { return null; }
        const res = await fetch(url);
        if (!res.ok) { throw new Error(`Azure Storage returned ${res.status}`); }
        return res.blob();
    }

    /** Read-only SAS URL of the blob behind a connected document, or null. */
    public getUrl(id: string): string | null {
        return this.urls.get(id) ?? null;
    }

    public has(id: string): boolean {
        return this.urls.has(id);
    }

    //*************************************************************************
    //  List Blobs (REST)
    //*************************************************************************
    public async list(): Promise<AzureBlob[]> {
        const s = this.settings;
        const base = s.containerUrl.trim().replace(/\/+$/, '');
        const sas = s.sasToken.trim().replace(/^\?/, '');
        const out: AzureBlob[] = [];
        let marker = '';
        do {
            const q = `restype=container&comp=list&maxresults=1000&prefix=${encodeURIComponent(s.prefix.trim())}` +
                (marker ? `&marker=${encodeURIComponent(marker)}` : '');
            const res = await fetch(`${base}?${q}&${sas}`);
            if (!res.ok) {
                throw new Error(res.status === 403 || res.status === 401
                    ? 'Access denied. Check the SAS token (needs read + list) and that it has not expired.'
                    : `Azure Storage returned ${res.status}`);
            }
            const xml = new DOMParser().parseFromString(await res.text(), 'application/xml');
            xml.querySelectorAll('Blob').forEach(b => {
                const name = b.querySelector('Name')?.textContent ?? '';
                if (name && !name.endsWith('/')) {
                    out.push({
                        name,
                        size: Number(b.querySelector('Content-Length')?.textContent ?? 0),
                        modified: b.querySelector('Last-Modified')?.textContent ?? ''
                    });
                }
            });
            marker = xml.querySelector('NextMarker')?.textContent ?? '';
            this.progress = `Reading folder… ${out.length} files`;
        } while (marker);
        return out;
    }

    //*************************************************************************
    //  Classification (structure of the file name and its folder)
    //*************************************************************************
    public toDocument(b: AzureBlob, index: number): FoundDocument | null {
        const file = b.name.split('/').pop() ?? b.name;
        const ext = (file.split('.').pop() ?? '').toLowerCase() as FileExt;
        console.log('Processing blob:', file, '| Extension:', ext, '| Supported?', EXTENSIONS.includes(ext));
        if (!EXTENSIONS.includes(ext)) { 
            console.log('  → FILTERED: unsupported extension');
            return null; 
        }
        const haystack = b.name.toLowerCase();
        const id = `az${index}`;
        const t = this.typeOf(file, haystack);
        const land: Landscape = /as-?is|ecc|legacy|archive/.test(haystack) ? 'asis'
            : /to-?be|bj8|s4|s\/4|signavio|digital core/.test(haystack) ? 'tobe' : t === 'sdr' || t === 'kt' ? 'asis' : 'tobe';
        const l2 = L2_PROCESSES.filter(x => haystack.includes(x.id.toLowerCase())).map(x => x.id);
        const codes = (b.name.match(/\b(?:LSC|DS|M|DC)-\d{3}(?:-\d{3})?\b|\bBJ8\b|\bHCVC\d*\b/gi) ?? []).map(c => c.toUpperCase());
        const sig = [l2.length ? 1 : 0, t !== 'other' ? 1 : 0, codes.length ? 1 : 0, 1, 1];
        const conf = Math.min(95, 35 + sig.reduce((a, v) => a + v, 0) * 12);
        this.urls.set(id, this.blobUrl(b.name));
        return {
            id, t, land, ext, name: file, src: CONNECTED_FOLDER_KEY,
            path: b.name.split('/').slice(0, -1).join(' › ') || 'Azure Storage',
            l2, codes: [...new Set(codes)], conf,
            why: t === 'other' ? 'Read from the connected Azure Storage folder; no Explore document pattern recognised.'
                : `Read from the connected Azure Storage folder; recognised as ${t.toUpperCase()}${l2.length ? ' for ' + l2.join(', ') : ''}.`,
            mod: b.modified ? new Date(b.modified).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—',
            by: 'Azure Storage', sig
        };
    }

    private typeOf(file: string, haystack: string): DocType {
        const f = file.toLowerCase();
        if (/\bbpd\b|bpd[_\- ]/.test(f)) { 
            console.log('MATCHED BPD:', file);
            return 'bpd'; 
        }
        if (/\bcdd\b|cdd[_\- ]|\bscd\b|\bpdd\b/.test(f)) { 
            console.log('MATCHED CDD:', file);
            return 'cdd'; 
        }
        if (/\bsdr\b|sdr[_\- ]/.test(f)) { return 'sdr'; }
        if (/\bkt\b|kt[_\- ]|knowledge transfer/.test(f)) { return 'kt'; }
        if (/process manual|\.bpmn$|signavio|process flow/.test(f) || /process manuals?\//.test(haystack)) { return 'flow'; }
        // Debug: log unrecognized files
        console.log('Unrecognized file:', file, '| Type: other');
        return 'other';
    }

    private blobUrl(name: string): string {
        const base = this.settings.containerUrl.trim().replace(/\/+$/, '');
        const sas = this.settings.sasToken.trim().replace(/^\?/, '');
        return `${base}/${name.split('/').map(encodeURIComponent).join('/')}?${sas}`;
    }

    private folderCount(blobs: AzureBlob[]): number {
        return new Set(blobs.map(b => b.name.split('/').slice(0, -1).join('/'))).size;
    }

    //*************************************************************************
    //  Settings
    //*************************************************************************
    private loadSettings(): AzureFolderSettings {
        const cfg: Partial<AzureFolderSettings> = this.injector?.get(AppConfigService, null)?.getConfig('azureStorage') ?? {};
        return {
            containerUrl: cfg.containerUrl ?? '',
            prefix: cfg.prefix ?? '',
            sasToken: cfg.sasToken ?? ''
        };
    }

    private fail(message: string): Error {
        this.error = message;
        this.progress = '';
        return new Error(message);
    }
}
